import React, { useState, useEffect } from 'react';
import {
  Phone,
  PhoneCall,
  PhoneMissed,
  PhoneOff,
  Users,
  Clock,
  Activity,
  Server,
  Shield,
  HardDrive,
  Cpu,
  Wifi,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Play,
  Pause,
  Volume2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { dashboardAPI, extensionsAPI, sipTrunksAPI, callQueuesAPI, eventLogsAPI } from '../services/api';

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dashboardStats, setDashboardStats] = useState({
    activeExtensions: 0,
    totalExtensions: 0,
    activeCalls: 0,
    callsToday: 0,
    missedCalls: 0,
    avgCallDuration: "0:00",
    sipTrunksOnline: 0,
    sipTrunksTotal: 0,
    queuedCalls: 0,
    systemHealth: 100
  });
  const [systemInfo, setSystemInfo] = useState({
    name: "ELE145",
    version: "v20.0.1",
    license: "Enterprise",
    fqdn: "pbx.ele145.com",
    publicIp: "0.0.0.0",
    localIp: "0.0.0.0",
    uptime: "0 days, 00:00:00",
    cpuUsage: 0,
    memoryUsage: 0
  });
  const [activeCalls, setActiveCalls] = useState([]);
  const [sipTrunks, setSipTrunks] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [callQueues, setCallQueues] = useState([]);
  const [eventLogs, setEventLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, statusRes, callsRes, trunksRes, extsRes, queuesRes, eventsRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getSystemStatus(),
        dashboardAPI.getActiveCalls(),
        sipTrunksAPI.list(),
        extensionsAPI.list(),
        callQueuesAPI.list(),
        dashboardAPI.getRecentEvents()
      ]);

      setDashboardStats({
        activeExtensions: statsRes.data.active_extensions,
        totalExtensions: statsRes.data.total_extensions,
        activeCalls: statsRes.data.active_calls,
        callsToday: statsRes.data.calls_today,
        missedCalls: statsRes.data.missed_calls,
        avgCallDuration: statsRes.data.avg_call_duration,
        sipTrunksOnline: statsRes.data.sip_trunks_online,
        sipTrunksTotal: statsRes.data.sip_trunks_total,
        queuedCalls: statsRes.data.queued_calls,
        systemHealth: statsRes.data.system_health
      });

      setSystemInfo({
        name: statusRes.data.name,
        version: statusRes.data.version,
        license: statusRes.data.license,
        fqdn: statusRes.data.fqdn,
        publicIp: statusRes.data.public_ip,
        localIp: statusRes.data.local_ip,
        uptime: statusRes.data.uptime,
        cpuUsage: statusRes.data.cpu_usage,
        memoryUsage: statusRes.data.memory_usage
      });

      // Transform active calls
      setActiveCalls(callsRes.data.map(call => ({
        id: call.id,
        from: call.from_number,
        fromName: call.from_name,
        to: call.to_number,
        toName: call.to_name,
        duration: call.duration,
        status: call.status,
        trunk: call.trunk_name
      })));

      // Transform SIP trunks
      setSipTrunks(trunksRes.data.map(trunk => ({
        id: trunk.id,
        name: trunk.name,
        provider: trunk.provider,
        status: trunk.status,
        channels: trunk.channels,
        usedChannels: trunk.used_channels
      })));

      // Transform extensions
      setExtensions(extsRes.data.map(ext => ({
        id: ext.id,
        name: ext.name,
        department: ext.department,
        status: ext.status
      })));

      // Transform call queues
      setCallQueues(queuesRes.data.map(queue => ({
        id: queue.id,
        name: queue.name,
        extension: queue.extension,
        status: queue.status,
        currentCalls: queue.current_calls,
        waitingCalls: queue.waiting_calls
      })));

      setEventLogs(eventsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh data every 30 seconds
    const refreshInterval = setInterval(fetchData, 30000);
    return () => clearInterval(refreshInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statCards = [
    {
      title: 'Active Calls',
      value: dashboardStats.activeCalls,
      icon: PhoneCall,
      color: 'from-green-500 to-emerald-400',
      change: '+12%',
      positive: true
    },
    {
      title: 'Calls Today',
      value: dashboardStats.callsToday,
      icon: Phone,
      color: 'from-blue-500 to-cyan-400',
      change: '+8%',
      positive: true
    },
    {
      title: 'Missed Calls',
      value: dashboardStats.missedCalls,
      icon: PhoneMissed,
      color: 'from-red-500 to-rose-400',
      change: '-5%',
      positive: true
    },
    {
      title: 'Active Extensions',
      value: `${dashboardStats.activeExtensions}/${dashboardStats.totalExtensions}`,
      icon: Users,
      color: 'from-purple-500 to-violet-400',
      change: '+2',
      positive: true
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'busy': return 'bg-red-500';
      case 'away': return 'bg-yellow-500';
      case 'dnd': return 'bg-orange-500';
      case 'offline': return 'bg-gray-500';
      case 'talking': return 'bg-green-500';
      case 'queue': return 'bg-blue-500';
      case 'ringing': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'online': return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Online</Badge>;
      case 'offline': return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Offline</Badge>;
      case 'talking': return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Talking</Badge>;
      case 'queue': return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Queue</Badge>;
      case 'ringing': return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Ringing</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getLogLevelColor = (level) => {
    switch (level) {
      case 'info': return 'text-blue-400';
      case 'warning': return 'text-yellow-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 mt-1">Welcome to ELE145 Admin Console</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-2xl font-mono text-white">
              {currentTime.toLocaleTimeString()}
            </p>
            <p className="text-sm text-gray-400">
              {currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="bg-[#12121a] border-[#1e1e2d] hover:border-[#2a2a3d] transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-400">{stat.title}</p>
                    <p className="text-3xl font-bold text-white mt-2">{stat.value}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {stat.positive ? (
                        <ArrowUpRight className="w-4 h-4 text-green-400" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-400" />
                      )}
                      <span className={`text-sm ${stat.positive ? 'text-green-400' : 'text-red-400'}`}>
                        {stat.change}
                      </span>
                      <span className="text-sm text-gray-500">vs last hour</span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Calls */}
        <Card className="lg:col-span-2 bg-[#12121a] border-[#1e1e2d]">
          <CardHeader className="border-b border-[#1e1e2d]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <PhoneCall className="w-5 h-5 text-green-400" />
                Active Calls
              </CardTitle>
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                {activeCalls.length} Active
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#1e1e2d]">
              {activeCalls.map((call) => (
                <div key={call.id} className="p-4 hover:bg-[#1e1e2d]/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(call.status)} animate-pulse`}></div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{call.fromName}</span>
                          <span className="text-gray-500">→</span>
                          <span className="text-white font-medium">{call.toName}</span>
                        </div>
                        <p className="text-sm text-gray-500">
                          {call.from} → {call.to}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-white font-mono">{call.duration}</p>
                        {getStatusBadge(call.status)}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-[#2a2a3d]">
                          <Volume2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20">
                          <PhoneOff className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {activeCalls.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <Phone className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No active calls</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* System Status */}
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardHeader className="border-b border-[#1e1e2d]">
            <CardTitle className="text-white flex items-center gap-2">
              <Server className="w-5 h-5 text-blue-400" />
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-400">CPU Usage</span>
                </div>
                <span className="text-sm text-white">{systemInfo.cpuUsage}%</span>
              </div>
              <Progress value={systemInfo.cpuUsage} className="h-2 bg-[#1e1e2d]" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-400">Memory</span>
                </div>
                <span className="text-sm text-white">{systemInfo.memoryUsage}%</span>
              </div>
              <Progress value={systemInfo.memoryUsage} className="h-2 bg-[#1e1e2d]" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-400">System Health</span>
                </div>
                <span className="text-sm text-green-400">{dashboardStats.systemHealth}%</span>
              </div>
              <Progress value={dashboardStats.systemHealth} className="h-2 bg-[#1e1e2d]" />
            </div>

            <div className="pt-4 border-t border-[#1e1e2d] space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">FQDN</span>
                <span className="text-white font-mono">{systemInfo.fqdn}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Public IP</span>
                <span className="text-white font-mono">{systemInfo.publicIp}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">License</span>
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                  {systemInfo.license}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Uptime</span>
                <span className="text-white">{systemInfo.uptime}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* SIP Trunks Status */}
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardHeader className="border-b border-[#1e1e2d]">
            <CardTitle className="text-white flex items-center gap-2">
              <Phone className="w-5 h-5 text-purple-400" />
              SIP Trunks
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#1e1e2d]">
              {sipTrunks.map((trunk) => (
                <div key={trunk.id} className="p-4 hover:bg-[#1e1e2d]/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${trunk.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <div>
                        <p className="text-white font-medium">{trunk.name}</p>
                        <p className="text-xs text-gray-500">{trunk.provider}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-white">{trunk.usedChannels}/{trunk.channels}</p>
                      <p className="text-xs text-gray-500">channels</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Call Queues */}
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardHeader className="border-b border-[#1e1e2d]">
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Call Queues
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#1e1e2d]">
              {callQueues.map((queue) => (
                <div key={queue.id} className="p-4 hover:bg-[#1e1e2d]/50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-white font-medium">{queue.name}</p>
                      <p className="text-xs text-gray-500">Ext: {queue.extension}</p>
                    </div>
                    <Badge className={queue.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                      {queue.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <PhoneCall className="w-3 h-3 text-green-400" />
                      <span className="text-gray-400">{queue.currentCalls} active</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-yellow-400" />
                      <span className="text-gray-400">{queue.waitingCalls} waiting</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Events */}
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardHeader className="border-b border-[#1e1e2d]">
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-orange-400" />
              Recent Events
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#1e1e2d] max-h-[300px] overflow-y-auto">
              {eventLogs.slice(0, 5).map((log) => (
                <div key={log.id} className="p-4 hover:bg-[#1e1e2d]/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 ${getLogLevelColor(log.level)}`}>
                      {log.level === 'error' ? (
                        <XCircle className="w-4 h-4" />
                      ) : log.level === 'warning' ? (
                        <AlertTriangle className="w-4 h-4" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{log.message}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {log.timestamp.split(' ')[1]}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Extension Status Overview */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardHeader className="border-b border-[#1e1e2d]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Extension Status Overview
            </CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-400">Online ({extensions.filter(e => e.status === 'online').length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-400">Busy ({extensions.filter(e => e.status === 'busy').length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm text-gray-400">Away ({extensions.filter(e => e.status === 'away').length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                <span className="text-sm text-gray-400">Offline ({extensions.filter(e => e.status === 'offline').length})</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {extensions.slice(0, 12).map((ext) => (
              <div
                key={ext.id}
                className="bg-[#1e1e2d] rounded-lg p-3 hover:bg-[#2a2a3d] transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(ext.status)}`}></div>
                  <span className="text-white font-mono text-sm">{ext.id}</span>
                </div>
                <p className="text-sm text-white truncate">{ext.name}</p>
                <p className="text-xs text-gray-500 truncate">{ext.department}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
